// MaturaTrigSinCosIdentityClosedTask.js
// Zadanie maturalne – zamknięte (trygonometria):
// Upraszczanie wyrażenia z sin i cos przy użyciu:
//  - tożsamości sin^2 α + cos^2 α = 1
//  - definicji tanga: tan α = sin α / cos α
//
// Wynik jest jedną z wartości: sin α, cos α, tan α lub 1.
// Odpowiedzi w formacie A, B, C, D (ABCDInput).

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import ABCDInput from "../../../../components/inputs/ABCDInput";

const LETTERS = ["A", "B", "C", "D"];

const randInt = (min, max) =>
  Math.floor(Math.random() * (max - min + 1)) + min;

const pick = (arr) => arr[randInt(0, arr.length - 1)];

function shuffle(array) {
  const arr = [...array];
  for (let i = arr.length - 1; i > 0; i -= 1) {
    const j = randInt(0, i);
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

// dozwolone kąty: ostre, wielokrotności 5°, bez 0°, 30°, 45°, 60°, 90°
const ANGLES = [];
for (let a = 5; a <= 85; a += 5) {
  if (a === 30 || a === 45 || a === 60) continue;
  ANGLES.push(a);
}

// ======================= GENERATOR =======================

/**
 * Wariant „oryginał” – matura podstawowa, czerwiec 2025, zadanie 17:
 *   (sin^3 25° + sin25°·cos^2 25°) / cos25° = sin25°(sin²+cos²)/cos25° = tg 25°  (C).
 */
function createJune2025OriginalTask() {
  const angleLatex = "25^\\circ";
  const options = [
    { kind: "sin", latex: "\\sin 25^\\circ", label: "A" },
    { kind: "cos", latex: "\\cos 25^\\circ", label: "B" },
    { kind: "tan", latex: "\\tan 25^\\circ", label: "C" },
    { kind: "one", latex: "1", label: "D" },
  ];

  return {
    type: "MaturaTrigSinCosIdentityClosedTask",
    data: {
      angle: 25,
      angleLatex,
      exprLatex:
        "\\dfrac{\\sin^3 25^\\circ + \\sin 25^\\circ\\cdot\\cos^2 25^\\circ}{\\cos 25^\\circ}",
      variant: "tan",
      options,
      correctIndex: 2,
    },
    source: {
      variant: "original",
      exam: "MATURA",
      session: "2025_CZE",
      taskNumber: 17,
    },
  };
}

export function generateTask(generationOptions = {}) {
  if (
    generationOptions.variant === "original" &&
    generationOptions.session === "2025_CZE"
  ) {
    return createJune2025OriginalTask();
  }

  const angle = pick(ANGLES);
  const angleLatex = `${angle}^\\circ`;

  const sin = `\\sin ${angleLatex}`;
  const cos = `\\cos ${angleLatex}`;
  const sin2 = `\\sin^2 ${angleLatex}`;
  const cos2 = `\\cos^2 ${angleLatex}`;
  const sin3 = `\\sin^3 ${angleLatex}`;
  const cos3 = `\\cos^3 ${angleLatex}`;

  // warianty wyniku: sin, cos, tan, 1
  const VARIANTS = ["sin", "cos", "tan", "one"];
  const variant = pick(VARIANTS);

  let exprLatex;
  let explanationVariant; // żeby renderExplanation wiedział, co robić

  if (variant === "tan") {
    // przykład z matury:
    // (sin^3 α + sin α cos^2 α) / cos α = tan α
    exprLatex = `\\dfrac{${sin3} + ${sin}\\cdot${cos2}}{${cos}}`;
    explanationVariant = "tan";
  } else if (variant === "sin") {
    // (sin^3 α + sin α cos^2 α) / (sin^2 α + cos^2 α) = sin α
    exprLatex = `\\dfrac{${sin3} + ${sin}\\cdot${cos2}}{${sin2} + ${cos2}}`;
    explanationVariant = "sin";
  } else if (variant === "cos") {
    // (cos^3 α + cos α sin^2 α) / (sin^2 α + cos^2 α) = cos α
    exprLatex = `\\dfrac{${cos3} + ${cos}\\cdot${sin2}}{${sin2} + ${cos2}}`;
    explanationVariant = "cos";
  } else {
    // wariant = "one"
    // ((sin^2 α + cos^2 α)^2) / (sin^2 α + cos^2 α) = 1
    exprLatex = `\\dfrac{\\bigl(${sin2} + ${cos2}\\bigr)^2}{${sin2} + ${cos2}}`;
    explanationVariant = "one";
  }

  // budujemy opcje ABCD: sin α, cos α, tan α, 1
  const baseOptions = [
    { kind: "sin", latex: sin },
    { kind: "cos", latex: cos },
    { kind: "tan", latex: `\\tan ${angleLatex}` },
    { kind: "one", latex: "1" },
  ];

  const shuffled = shuffle(baseOptions);
  const options = shuffled.map((opt, idx) => ({
    ...opt,
    label: LETTERS[idx],
  }));

  const correctIndex = options.findIndex((o) => o.kind === variant);

  return {
    type: "MaturaTrigSinCosIdentityClosedTask",
    data: {
      angle,
      angleLatex,
      exprLatex,
      variant: explanationVariant,
      options,
      correctIndex,
    },
  };
}

// ======================= RENDER TREŚCI =======================

export function renderTask({ data }) {
  const { exprLatex, angleLatex, options } = data;

  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p>
        <strong>Zadanie maturalne – zamknięte.</strong>
      </p>
      <p>
        Dokończ zdanie. Wybierz właściwą odpowiedź spośród podanych.
      </p>
      <p>
        Liczba{" "}
        <InlineMath math={exprLatex} /> jest równa:
      </p>
      <div style={{ marginTop: 8 }}>
        {options.map((opt) => (
          <div key={opt.label} style={{ marginBottom: 10 }}>
            <InlineMath math={`${opt.label}.\\; ${opt.latex}`} />
          </div>
        ))}
      </div>
    </div>
  );
}

// ======================= INPUT (ABCD) =======================

export function renderInput({ data }, userAnswer, onChange, disabled) {
  const { options } = data;

  const choiceIndex =
    userAnswer && typeof userAnswer.choiceIndex === "number"
      ? userAnswer.choiceIndex
      : null;

  const letterOptions = options.map((opt, idx) => ({
    value: idx,
    label: opt.label,
    textNode: opt.label,
  }));

  const handleChange = (idx) => {
    const opt = options[idx];
    onChange?.({
      choiceIndex: idx,
      label: opt.label,
      latex: opt.latex,
      kind: opt.kind,
    });
  };

  return (
    <ABCDInput
      options={letterOptions}
      value={choiceIndex}
      onChange={handleChange}
      disabled={disabled}
    />
  );
}

// ======================= SPRAWDZANIE =======================

export function checkAnswer({ data }, userAnswer) {
  if (
    !userAnswer ||
    typeof userAnswer.choiceIndex !== "number" ||
    !Number.isInteger(userAnswer.choiceIndex)
  ) {
    return false;
  }
  return userAnswer.choiceIndex === data.correctIndex;
}

// ======================= POPRAWNA ODPOWIEDŹ =======================

export function renderAnswer({ data }) {
  const { options, correctIndex } = data;
  const correct = options[correctIndex];

  return <InlineMath math={`${correct.label}.\\; ${correct.latex}`} />;
}

// ======================= ODPOWIEDŹ UŻYTKOWNIKA =======================

export function renderUserAnswer(userAnswer) {
  if (
    !userAnswer ||
    typeof userAnswer.choiceIndex !== "number" ||
    !userAnswer.label ||
    userAnswer.latex == null
  ) {
    return <i>(brak odpowiedzi)</i>;
  }

  const { label, latex } = userAnswer;

  return (
    <div style={{ marginTop: "1rem" }}>
      <span>Twoja odpowiedź: </span>
      <InlineMath math={`${label}.\\; ${latex}`} />
    </div>
  );
}

// ======================= WYJAŚNIENIE =======================

export function renderExplanation({ data }) {
  const { angleLatex, variant } = data;

  const sin = `\\sin ${angleLatex}`;
  const cos = `\\cos ${angleLatex}`;
  const sin2 = `\\sin^2 ${angleLatex}`;
  const cos2 = `\\cos^2 ${angleLatex}`;
  const sin3 = `\\sin^3 ${angleLatex}`;
  const cos3 = `\\cos^3 ${angleLatex}`;

  if (variant === "tan") {
    // (sin^3 + sin cos^2)/cos
    return (
      <div className="space-y-3" style={{ textAlign: "left" }}>
        <p>
          <strong>Krok 1.</strong> W liczniku wyłączamy{" "}
          <InlineMath math={sin} /> przed nawias.
        </p>
        <BlockMath
          math={`\\dfrac{${sin3} + ${sin}\\cdot${cos2}}{${cos}} = \\dfrac{${sin}\\bigl(${sin2} + ${cos2}\\bigr)}{${cos}}`}
        />
        <p>
          <strong>Krok 2.</strong> Korzystamy z tożsamości{" "}
          <InlineMath math={'\\sin^2\\alpha + \\cos^2\\alpha = 1'} />.
        </p>
        <BlockMath
          math={`\\sin^2 ${angleLatex} + \\cos^2 ${angleLatex} = 1`}
        />
        <BlockMath
          math={`\\dfrac{${sin}\\cdot 1}{${cos}} = \\dfrac{${sin}}{${cos}}`}
        />
        <p>
          <strong>Krok 3.</strong> Z definicji tangensa{" "}
          <InlineMath math={'\\tan\\alpha = \\dfrac{\\sin\\alpha}{\\cos\\alpha}'} />{" "}
          otrzymujemy:
        </p>
        <BlockMath math={`\\dfrac{${sin}}{${cos}} = \\tan ${angleLatex}`} />
      </div>
    );
  }

  if (variant === "sin") {
    // (sin^3 + sin cos^2)/(sin^2 + cos^2)
    return (
      <div className="space-y-3" style={{ textAlign: "left" }}>
        <p>
          <strong>Krok 1.</strong> W liczniku wyłączamy{" "}
          <InlineMath math={sin} /> przed nawias.
        </p>
        <BlockMath
          math={`\\dfrac{${sin3} + ${sin}\\cdot${cos2}}{${sin2} + ${cos2}} = \\dfrac{${sin}\\bigl(${sin2} + ${cos2}\\bigr)}{${sin2} + ${cos2}}`}
        />
        <p>
          <strong>Krok 2.</strong> Skracamy przez{" "}
          <InlineMath math={'\\sin^2\\alpha + \\cos^2\\alpha'} /> i używamy
          tożsamości jedynkowej.
        </p>
        <BlockMath
          math={`\\dfrac{${sin}\\cdot 1}{1} = ${sin}`}
        />
      </div>
    );
  }

  if (variant === "cos") {
    // (cos^3 + cos sin^2)/(sin^2 + cos^2)
    return (
      <div className="space-y-3" style={{ textAlign: "left" }}>
        <p>
          <strong>Krok 1.</strong> W liczniku wyłączamy{" "}
          <InlineMath math={cos} /> przed nawias.
        </p>
        <BlockMath
          math={`\\dfrac{${cos3} + ${cos}\\cdot${sin2}}{${sin2} + ${cos2}} = \\dfrac{${cos}\\bigl(${sin2} + ${cos2}\\bigr)}{${sin2} + ${cos2}}`}
        />
        <p>
          <strong>Krok 2.</strong> Skracamy licznik i mianownik przez{" "}
          <InlineMath math={'\\sin^2\\alpha + \\cos^2\\alpha'} /> i podstawiamy{" "}
          <InlineMath math={'\\sin^2\\alpha + \\cos^2\\alpha = 1'} />.
        </p>
        <BlockMath
          math={`\\dfrac{${cos}\\cdot 1}{1} = ${cos}`}
        />
      </div>
    );
  }

  // variant === "one"
  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p>
        <strong>Krok 1.</strong> Zapisujemy wyrażenie:
      </p>
      <BlockMath
        math={`\\dfrac{\\bigl(${sin2} + ${cos2}\\bigr)^2}{${sin2} + ${cos2}}`}
      />
      <p>
        <strong>Krok 2.</strong> Korzystamy z tożsamości{" "}
        <InlineMath math={'\\sin^2\\alpha + \\cos^2\\alpha = 1'} />.
      </p>
      <BlockMath
        math={`\\sin^2 ${angleLatex} + \\cos^2 ${angleLatex} = 1`}
      />
      <BlockMath
        math={`\\dfrac{1^2}{1} = 1`}
      />
    </div>
  );
}
